/**
 * Renderer-agnostic metadata legend overlay.
 *
 * Phylocanvas paints its own legend inside its WebGL gear-menu; the SVG
 * (`theiaphyloviewer`) renderer does not, so the app draws a small HTML legend
 * card into the tree container as a sibling overlay. `renderTree()` blanks the
 * container on every render, so the overlay follows the same self-build-per-
 * render contract the appearance panel and watermark use: it is repainted from a
 * stored model each render rather than relying on the node persisting.
 *
 * The model functions are DOM-free so they unit-test in the `node` runner; the
 * `MetadataLegend` class is the thin DOM glue, guarded to no-op without a
 * document (it is browser-verified rather than unit-tested).
 */

const LEGEND_POSITIONS = new Set([
  'top-left',
  'top-right',
  'bottom-left',
  'bottom-right',
]);

const CONTAINER_ID = 'tree-container';

export interface LegendValueEntry {
  value: string;
  colour: string;
}

export interface LegendField {
  field: string;
  entries: LegendValueEntry[];
}

export interface LegendConfig {
  visible: boolean;
  columns: number;
  position: string;
}

/** A metadata-block cell, structurally matching `computeMetadataBlocks` output. */
type LegendBlockCell = { label?: string; colour?: string };
type LegendBlocks = Record<string, Record<string, LegendBlockCell>>;

/**
 * Collapse the per-leaf block cells into one legend row per distinct
 * value/colour pair, for each requested field in order. Cells with an empty
 * value or colour are skipped (they are hidden in the tree too), and a field
 * whose every cell is missing or empty is dropped so the legend never shows an
 * empty section.
 */
export function buildLegendModel(
  fields: string[],
  blocks: LegendBlocks,
): LegendField[] {
  const leafIds = Object.keys(blocks);
  const model: LegendField[] = [];
  for (const field of fields) {
    const seen = new Map<string, string>();
    for (const leafId of leafIds) {
      const cell = blocks[leafId]?.[field];
      if (!cell) continue;
      const value = cell.label;
      const colour = cell.colour;
      if (!value || !colour) continue;
      if (!seen.has(value)) seen.set(value, colour);
    }
    if (seen.size === 0) continue;
    model.push({
      field,
      entries: [...seen.entries()].map(([value, colour]) => ({ value, colour })),
    });
  }
  return model;
}

/** Clamp a saved column count into the [1, 4] range phylocanvas exposes. */
export function clampLegendColumns(columns: unknown): number {
  const n =
    typeof columns === 'number' && Number.isFinite(columns)
      ? Math.round(columns)
      : 2;
  return Math.min(4, Math.max(1, n));
}

/** Map a saved corner to its CSS class, defaulting to the top-left corner. */
export function legendPositionClass(position: unknown): string {
  const pos =
    typeof position === 'string' && LEGEND_POSITIONS.has(position)
      ? position
      : 'top-left';
  return `legend-pos-${pos}`;
}

/**
 * The DOM overlay. Stateful so it can replay its last model when `renderTree()`
 * clears the container: `update` stores the model and paints, `remount` repaints
 * the stored model after a render wipe, and `dispose` is the teardown hook that
 * removes the overlay and forgets the model. All three no-op without a document,
 * keeping the class safe to construct in the non-DOM test runner.
 */
export class MetadataLegend {
  private el: HTMLElement | null = null;
  private legend: LegendConfig | null = null;
  private fields: string[] = [];
  private blocks: LegendBlocks = {};

  /**
   * @param onEntryClick optional click handler; when supplied, each legend entry
   *   becomes a clickable selector that reports its `(field, value)`. The class
   *   stays decoupled from selection -- the caller decides what a click does.
   *   Omitted on the Tableau surface, where the legend stays display-only.
   */
  constructor(
    private readonly onEntryClick?: (field: string, value: string) => void,
  ) {}

  update(
    legend: LegendConfig | null,
    fields: string[],
    blocks: LegendBlocks,
  ): void {
    this.legend = legend;
    this.fields = fields;
    this.blocks = blocks;
    this.render();
  }

  remount(): void {
    this.render();
  }

  dispose(): void {
    this.el?.remove();
    this.el = null;
    this.legend = null;
    this.fields = [];
    this.blocks = {};
  }

  private render(): void {
    if (typeof document === 'undefined') return;
    this.el?.remove();
    this.el = null;

    if (!this.legend || !this.legend.visible) return;
    const model = buildLegendModel(this.fields, this.blocks);
    if (model.length === 0) return;

    const container = document.getElementById(CONTAINER_ID);
    if (!container) return;
    const doc = container.ownerDocument;
    if (!doc) return;

    const card = doc.createElement('div');
    card.className = `metadata-legend ${legendPositionClass(this.legend.position)}`;
    card.style.setProperty(
      '--legend-columns',
      String(clampLegendColumns(this.legend.columns)),
    );

    for (const { field, entries } of model) {
      const section = doc.createElement('div');
      section.className = 'metadata-legend-section';

      const heading = doc.createElement('div');
      heading.className = 'metadata-legend-field';
      heading.textContent = field;
      section.appendChild(heading);

      const grid = doc.createElement('div');
      grid.className = 'metadata-legend-entries';
      for (const { value, colour } of entries) {
        const entry = doc.createElement('div');
        entry.className = 'metadata-legend-entry';

        const swatch = doc.createElement('span');
        swatch.className = 'metadata-legend-swatch';
        swatch.style.backgroundColor = colour;

        const label = doc.createElement('span');
        label.className = 'metadata-legend-label';
        label.textContent = value;
        label.title = value;

        entry.appendChild(swatch);
        entry.appendChild(label);
        if (this.onEntryClick) {
          const onClick = this.onEntryClick;
          entry.style.cursor = 'pointer';
          entry.title = `Select tips where ${field} = ${value}`;
          entry.addEventListener('click', () => onClick(field, value));
        }
        grid.appendChild(entry);
      }
      section.appendChild(grid);
      card.appendChild(section);
    }

    container.appendChild(card);
    this.el = card;
  }
}
